import { z } from 'zod';
import { campaignQuestionSchema } from './campaign-question.ts';

const optionalText = z
  .string()
  .nullish()
  .transform((value) => value || undefined);
export const battleCodePattern = /^[a-zA-Z0-9_-]{4,64}$/;
export const battleSchema = z.object({
  id: z.string().min(1),
  inviteCode: z.string().regex(battleCodePattern),
  status: z.string(),
  kind: z.string(),
  hostLogin: z.string().min(1),
  kinds: z.array(z.string()).optional(),
  hostDisplayName: optionalText,
  guestDisplayName: optionalText,
  hostReady: z.boolean().default(false),
  guestReady: z.boolean().default(false),
  guestLogin: optionalText,
  winnerLogin: optionalText,
  loserLogin: optionalText,
  loseReason: optionalText,
  questionsPlayed: z.number().int().nonnegative(),
  createdAt: z.string(),
  startedAt: optionalText,
  finishedAt: optionalText,
});
const timing = {
  serverNow: optionalText,
  phase: optionalText,
  questionId: optionalText,
  questionStartedAt: optionalText,
  questionDeadlineAt: optionalText,
  nextQuestionAt: optionalText,
  correctTitleId: optionalText,
  answeredCount: z.number().int().nonnegative().optional(),
  participantCount: z.number().int().nonnegative().optional(),
};
export const battleSnapshotSchema = z.object({
  ...timing,
  battle: battleSchema,
  question: campaignQuestionSchema.nullish(),
  roundIndex: z.number().int().nonnegative().optional(),
  answers: z
    .record(z.string(), z.object({ titleId: z.string().optional(), correct: z.boolean().optional() }))
    .nullish()
    .transform((value) => value ?? {}),
});
export type BattleDto = z.infer<typeof battleSchema>;
export type BattleSnapshot = z.infer<typeof battleSnapshotSchema>;
const answersSchema = z.record(
  z.string(),
  z.object({ titleId: z.string().optional(), correct: z.boolean().optional() }),
);
export const battleEventSchema = z.discriminatedUnion('type', [
  z.object({ ...timing, type: z.literal('state'), battle: battleSchema }),
  z.object({
    ...timing,
    type: z.literal('player'),
    login: z.string(),
    event: z.string(),
    battle: battleSchema.optional(),
  }),
  z.object({
    ...timing,
    type: z.literal('round'),
    index: z.number().int().positive(),
    question: campaignQuestionSchema,
  }),
  z.object({ ...timing, type: z.literal('round_result'), index: z.number().int().positive(), answers: answersSchema }),
  z.object({ ...timing, type: z.literal('finished'), battle: battleSchema }),
  z.object({ ...timing, type: z.literal('ack'), actionId: z.string() }),
  z.object({ ...timing, type: z.literal('answer_received') }),
  z.object({
    type: z.literal('error'),
    code: z.string().optional(),
    actionId: z.string().optional(),
    message: z.string().optional(),
    error: z.string().optional(),
  }),
]);
export type BattleEvent = z.infer<typeof battleEventSchema>;
export type BattleCommand = { actionId: string } & (
  | { type: 'start' | 'ready' | 'unready' | 'forfeit' | 'leave' }
  | { type: 'answer'; questionId: string; titleId: string }
);
export const battleCreatePayload = (values: readonly string[]) => {
  const kinds = [
    ...new Set(
      z
        .array(z.enum(['film', 'series', 'game', 'anime']))
        .min(1)
        .parse(values),
    ),
  ];
  return { kinds };
};
